"use client";

import { trackInternationalLanguageSelectorLanguageClickClick } from "@faire/web-api--source/events/international/click/languageSelectorLanguageClick";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import { ILocaleKey } from "@faire/web-api--source/indigofair/locale/ILocaleKey";
import { SHOP_PREVIEW_OVERRIDE_KEY } from "@faire/web/common/consts/SHOP_PREVIEW_OVERRIDE_KEY";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web/common/localization";
import {
  ILocaleOption,
  languageOptionsWithDisambiguatedLocale,
} from "@faire/web/common/localization/languageOptions";
import { languageWithCountryOptions } from "@faire/web/common/localization/languageWithCountryOptions";
import {
  setLocale,
  setLocaleForCookie,
} from "@faire/web/common/localization/setLocale";
import { Path } from "@faire/web/common/Path";
import { appendFaireDirectBrandAliasQueryParam } from "@faire/web/common/routes/faire-direct/appendFaireDirectBrandAliasQueryParam";
import { isRobot } from "@faire/web/common/user-agent/isRobot";
import { Button } from "@faire/web/slate-deprecated/Button";
import { ButtonStyles } from "@faire/web/slate-deprecated/Button/styles";
import { Spacer } from "@faire/web/slate/spacing";
import { Link } from "@faire/web/ui/LinkAnchor";
import {
  localizePath,
  localizePathForRobot,
  removeLocaleFromUrl,
} from "@faire/web/ui/routing/util";
import { usePathname, useSearchParams } from "next/navigation";
import React, { useContext } from "react";
import styled from "styled-components";

import { RoutingContext } from "../routing/RoutingContext";

interface IProps {
  eventName: string;
  up?: boolean;
  eventKey?: string;
  availableLocales: ILocaleKey[];
  localeOverrideKey?: typeof SHOP_PREVIEW_OVERRIDE_KEY;
  userToken?: string;
  assignLocalizedUrl?: boolean;
}

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate-deprecated/Dropdown/Dropdown#Dropdown"
 */
export const LanguageDropdown = ({
  availableLocales,
  localeOverrideKey,
  eventName,
  eventKey,
  userToken,
  assignLocalizedUrl,
}: IProps) => {
  const {
    faireDirectBrandAlias,
    faireDirectBrandToken,
    faireDirectWidgetToken,
  } = useContext(RoutingContext);
  const { strictLocalize } = useStrictLocalization();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentPath = pathname
    ? new Path({
        pathname,
        search: searchParams ? searchParams.toString() : "",
      }).toString()
    : null;

  // 2025-07-15: Fix for help center which doesn't support nextjs router
  const location = getWindow()?.location;
  const legacyRouterCurrentPath = location
    ? new Path({
        pathname: location.pathname,
        search: location.search,
      }).toString()
    : "/support";

  const path = currentPath ?? legacyRouterCurrentPath;

  const languages = languageOptionsWithDisambiguatedLocale(availableLocales);

  const getUrlFromLanguageAndLocaleKey = (
    language: keyof typeof Language | undefined,
    localeKey?: ILocaleKey
  ) => {
    if (!language) {
      return "";
    }

    let url = !assignLocalizedUrl
      ? removeLocaleFromUrl(path)
      : localizePath(path, language, localeKey);
    if (faireDirectBrandAlias && faireDirectBrandToken) {
      url = appendFaireDirectBrandAliasQueryParam({
        path: url,
        faireDirectBrandAlias,
        faireDirectBrandToken,
        faireDirectWidgetToken,
      });
    }
    return url;
  };

  // TODO(kelvin.chu): once the MOVE_REFERRER_KEY_UP feature is stable,
  // the assignLocalizedUrl and useLinks props should be removed.
  // we should use ListLinks as the default while still updating the cookie
  // this may require some rework to the onClick function in ListLink
  const assignUrl = (
    language: keyof typeof Language,
    localeKey?: ILocaleKey
  ) => {
    const url = getUrlFromLanguageAndLocaleKey(language, localeKey);

    /**
     * It is necessary to pushState() first before calling location.assign()
     * to clear the referrer before sending a new request to the backend.
     *
     * When switching to English, we remove the locale from the pathname
     * (ie. https://faire.com/ instead of https://faire.com/en-us/).
     * If the user is not logged-in, and there is no locale in the URL,
     * the backend falls back to checking the referrer for the locale.
     * For example, if the referrer is https://faire.com/fr the backend
     * will return:
     *   "locale_key":{"country":"FRA","language":"FRA"}
     *
     * Since the user intentionally selected a new language,
     * we need the backend to ignore the referrer from the previous page.
     */
    getWindow()?.history.pushState(null, "", url);
    getWindow()?.location.assign(url);
  };

  const handleLanguageClick = (option: ILocaleOption) => () => {
    if (option.value.language === Language.LANGUAGE_UNKNOWN) {
      return;
    }
    if (option.value.language === undefined) {
      return;
    }

    const localeKey = option.value;
    const lang = option.value.language;

    if (localeOverrideKey) {
      setLocaleForCookie(lang, localeOverrideKey, localeKey);
      assignUrl(lang, localeKey);
    } else {
      setLocale(lang, userToken, localeKey).then(() => {
        assignUrl(lang, localeKey);
      });
    }

    if (eventKey) {
      trackInternationalLanguageSelectorLanguageClickClick(
        eventName,
        eventKey,
        option.value.language
      );
    }
  };

  if (isRobot()) {
    const languageWithCountry = languageWithCountryOptions(
      availableLocales,
      strictLocalize
    );

    return languageWithCountry.map((option) => {
      return (
        <React.Fragment key={option.label}>
          <ListLink
            to={localizePathForRobot(path, option.value)}
            skipPrependLocale
          >
            {option.label}
          </ListLink>
          <Spacer height="2x" />
        </React.Fragment>
      );
    });
  }

  return languages
    .filter((option) => option.value.language !== undefined)
    .map((languageOption) => {
      return (
        <React.Fragment key={languageOption.label}>
          <ListItem onClick={handleLanguageClick(languageOption)}>
            {languageOption.label}
          </ListItem>
          <Spacer height="2x" />
        </React.Fragment>
      );
    });
};

const ListItem = styled(Button).attrs({ variant: "tertiary" })`
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ListLink = styled(Link)`
  ${(props) => ButtonStyles({ $variant: "tertiary", theme: props.theme })}
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
